import React from 'react';
import { AlertVariant, ComponentSize } from '../../styles/constants';
import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

export interface AlertProps {
  variant?: AlertVariant;
  size?: ComponentSize;
  title?: string;
  children: React.ReactNode;
  dismissible?: boolean;
  onDismiss?: () => void;
  className?: string;
}

const Alert: React.FC<AlertProps> = ({
  variant = 'info',
  size = 'md',
  title,
  children,
  dismissible = false,
  onDismiss,
  className = '',
}) => {
  const baseClasses = `
    rounded-lg border flex items-start gap-3
    transition-all duration-200
  `;

  const sizeClasses = {
    xs: 'p-2 text-xs',
    sm: 'p-3 text-sm',
    md: 'p-4 text-sm',
    lg: 'p-6 text-base',
    xl: 'p-8 text-lg',
  };

  const variantClasses = {
    info: `
      bg-blue-50 border-blue-200 text-blue-800
    `,
    success: `
      bg-success-50 border-success-200 text-success-800
    `,
    warning: `
      bg-warning-50 border-warning-200 text-warning-800
    `,
    error: `
      bg-error-50 border-error-200 text-error-800
    `,
  };

  const iconMap = {
    info: InformationCircleIcon,
    success: CheckCircleIcon,
    warning: ExclamationTriangleIcon,
    error: XCircleIcon,
  };

  const iconColorClasses = {
    info: 'text-blue-500',
    success: 'text-success-500',
    warning: 'text-warning-500',
    error: 'text-error-500',
  };

  const IconComponent = iconMap[variant];

  const combinedClasses = `
    ${baseClasses}
    ${sizeClasses[size]}
    ${variantClasses[variant]}
    ${className}
  `;

  const handleDismiss = () => {
    if (dismissible && onDismiss) {
      onDismiss();
    }
  };

  return (
    <div className={combinedClasses} role="alert">
      <div className="flex-shrink-0">
        <IconComponent
          className={`h-5 w-5 ${iconColorClasses[variant]}`}
          aria-hidden="true"
        />
      </div>

      <div className="flex-1 min-w-0">
        {title && <h3 className="font-medium mb-1">{title}</h3>}
        <div className={title ? '' : 'mt-0'}>{children}</div>
      </div>

      {dismissible && (
        <div className="flex-shrink-0">
          <button
            onClick={handleDismiss}
            className={`
              inline-flex rounded-md p-1.5 
              focus:outline-none focus:ring-2 focus:ring-offset-2
              transition-colors duration-200
              ${variant === 'info' ? 'text-blue-500 hover:bg-blue-100 focus:ring-blue-500' : ''}
              ${variant === 'success' ? 'text-success-500 hover:bg-success-100 focus:ring-success-500' : ''}
              ${variant === 'warning' ? 'text-warning-500 hover:bg-warning-100 focus:ring-warning-500' : ''}
              ${variant === 'error' ? 'text-error-500 hover:bg-error-100 focus:ring-error-500' : ''}
            `}
            aria-label="알림 닫기"
          >
            <XMarkIcon className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      )}
    </div>
  );
};

export default Alert;
